import { ChevronUpDownIcon } from "@heroicons/react/24/outline";
import React, { Fragment, RefObject, useCallback, useEffect, useMemo, useRef, useState, KeyboardEvent } from "react";
import { Menu, Transition } from '@headlessui/react'

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

export function Dropdown(props: {
  options: { name: string, callback: () => void }[],
  // If no title is presented, we just use an up/down icon.
  title?: string,
  // which corner the dropdown will drop down from
  dropdownOrigin?: 'right' | 'left',
  // whether or not the dropdown is searchable
  searchable?: boolean,
  className?: string,
}) {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [hoveredOptionIndex, setHoveredOptionIndex] = useState<number | null>(null);
  const selectRef: RefObject<HTMLDivElement> = useRef(null);
  const filteredOptions = useMemo(() => {
    return searchText == '' ?
      props.options :
      props.options.filter(o => o.name.toLowerCase().includes(searchText.toLowerCase()))
  }, [searchText, props.options]);

  const handleKeyPress = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (open) {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const newIndex = hoveredOptionIndex !== null ? hoveredOptionIndex - 1 : -1;
        setHoveredOptionIndex((newIndex + filteredOptions.length) % filteredOptions.length);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        const newIndex = hoveredOptionIndex !== null ? hoveredOptionIndex + 1 : 0;
        setHoveredOptionIndex((newIndex + filteredOptions.length) % filteredOptions.length);
      } else if (e.key === 'Enter' && hoveredOptionIndex !== null) {
        e.preventDefault();
        setSearchText('');
        setHoveredOptionIndex(null);
        filteredOptions[hoveredOptionIndex].callback();
        setOpen(false);
      }
    }
  }, [open, hoveredOptionIndex, filteredOptions]);

  const openDropdown = useCallback(() => {
    setOpen(true);
    selectRef?.current?.focus();
  }, [selectRef]);

  // when our hoveredOptionIndex changes, scroll the active option in the dropdown into view
  useEffect(() => {
    selectRef?.current?.querySelector(".active")?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest', 
      inline: 'start'
    });
  }, [hoveredOptionIndex, selectRef]);

  return <div className={"relative inline-block text-left "+props.className}>
    <button 
      className="inline-flex justify-center w-auto shadow-sm px-3 py-1 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500"
      onClick={openDropdown}>
      {props.title || <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}
    </button>
    <div
      className="w-0 h-0" 
      ref={selectRef} 
      onMouseLeave={() => {setOpen(false); setHoveredOptionIndex(null);}}
      tabIndex={0}
      onKeyDown={handleKeyPress}>
      {open && 
        <div className={`origin-top-${props.dropdownOrigin || 'left'} z-[51] absolute ${props.dropdownOrigin || 'left'}-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none`}
          style={{ maxHeight: '350px', overflowY: 'auto', overflowX: 'clip' }}
        > 
          {props.searchable && 
          <input 
            className="m-2 p-2"
            type='text'
            value={searchText}
            onChange={(event) => {
              setSearchText(event.target.value)
            }}
            autoFocus
            placeholder="Search"
          />
          }
          {
            filteredOptions.map((option, i) => {
              return <div key={option.name}>
                <a className={classNames(
                  'hover:bg-gray-100 hover:text-gray-900 text-gray-700',
                  hoveredOptionIndex === i ? 'bg-gray-200 active' : '',
                  'block px-4 py-2 text-sm'
                )}
                onClick={(e) => {
                  e.preventDefault();
                  setHoveredOptionIndex(null);
                  setSearchText('');
                  setOpen(false);
                  option.callback();
                }}
                href="#">
                  {option.name}
                </a>
              </div>
            })
          }
        </div>
      }
    </div>
  </div>

  // Unreachable code. 
  // Headless UI doesn't work inside of web components :(
  // https://github.com/tailwindlabs/headlessui/discussions/874
  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className="inline-flex justify-center w-auto shadow-sm px-3 py-1 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500">
          {props.title}
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="origin-top-right absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1">
            {
              props.options.map((option) => {
                return <Menu.Item>
                  {({ active }) => (
                    <a
                      className={classNames(
                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                        'block px-4 py-2 text-sm'
                      )}
                      onClick={(e) => {
                        console.log('wtf');
                        e.preventDefault();
                        option.callback();
                      }}
                      href="#"
                    >
                      {option.name}
                    </a>
                  )}
                </Menu.Item>
              })
            }
          </div>
        </Menu.Items>
      </Transition>
    </Menu>    
  )
}
